import React, { useState, useEffect } from "react";
import WordCard from "../shared/WordCard";
import Quiz from "../shared/Quiz";
import PronunciationPractice from "../shared/PronunciationPractice";
import ReadAloudButton from "../shared/ReadAloudButton";
import SEO, { gradeSEOConfig, generateBreadcrumbs } from "../shared/SEO";

const Grade3 = ({ initialSection }) => {
  const [currentSection, setCurrentSection] = useState("grammar");

  useEffect(() => {
    if (initialSection) {
      setCurrentSection(initialSection);
    }
  }, [initialSection]);

  // Section definitions moved to GRADE_SECTIONS config

  const practiceWords = [
    { sinhala: "පාසල", english: "School", pronunciation: "paa-suh-luh" },
    { sinhala: "රෝහල", english: "Hospital", pronunciation: "roh-huh-luh" },
    { sinhala: "දුවනවා", english: "Running", pronunciation: "doo-vuh-nuh-vaa" },
    { sinhala: "ලියනවා", english: "Writing", pronunciation: "lee-yuh-nuh-vaa" },
    { sinhala: "ගුරුවරයා", english: "Teacher (male)", pronunciation: "goo-roo-vuh-ruh-yaa" },
    { sinhala: "වෛද්‍යවරයා", english: "Doctor", pronunciation: "vaid-yuh-vuh-ruh-yaa" },
    { sinhala: "සාරිය", english: "Saree", pronunciation: "saa-ree-yuh" },
    { sinhala: "කමිසය", english: "Shirt", pronunciation: "kuh-mee-suh-yuh" },
    { sinhala: "සත්තු වත්ත", english: "Zoo", pronunciation: "suhth-thoo vuhth-thuh" },
    { sinhala: "වෙරළ", english: "Beach", pronunciation: "veh-ruh-luh" },
  ];

  // Places vocabulary - expanded
  const places = [
    { sinhala: "පාසල", english: "School", pronunciation: "paa-suh-luh", image: "🏫" },
    { sinhala: "රෝහල", english: "Hospital", pronunciation: "roh-huh-luh", image: "🏥" },
    { sinhala: "වෙළෙඳසැල", english: "Shop", pronunciation: "veh-leh-duh-seh-luh", image: "🏪" },
    { sinhala: "උයන", english: "Garden", pronunciation: "oo-yuh-nuh", image: "🌳" },
    { sinhala: "පුස්තකාලය", english: "Library", pronunciation: "poos-thuh-kaa-luh-yuh", image: "📚" },
    { sinhala: "පන්සල", english: "Temple", pronunciation: "puhn-suh-luh", image: "🛕" },
    { sinhala: "බැංකුව", english: "Bank", pronunciation: "ben-koo-vuh", image: "🏦" },
    { sinhala: "තැපැල් කාර්යාලය", english: "Post Office", pronunciation: "theh-pehl kaar-yaa-luh-yuh", image: "🏤" },
    { sinhala: "පොලිස් ස්ථානය", english: "Police Station", pronunciation: "poh-lis sthaa-nuh-yuh", image: "🚔" },
    { sinhala: "දුම්රිය ස්ථානය", english: "Railway Station", pronunciation: "doom-ree-yuh sthaa-nuh-yuh", image: "🚂" },
    { sinhala: "ගුවන් තොටුපළ", english: "Airport", pronunciation: "goo-vuhn thoh-too-puh-luh", image: "✈️" },
    { sinhala: "වෙරළ", english: "Beach", pronunciation: "veh-ruh-luh", image: "🏖️" },
  ];

  // Actions/Verbs - expanded
  const actions = [
    { sinhala: "දුවනවා", english: "Running", pronunciation: "doo-vuh-nuh-vaa", image: "🏃" },
    { sinhala: "ඉගෙන ගන්නවා", english: "Learning", pronunciation: "ee-geh-nuh guhn-nuh-vaa", image: "📖" },
    { sinhala: "ලියනවා", english: "Writing", pronunciation: "lee-yuh-nuh-vaa", image: "✍️" },
    { sinhala: "කියවනවා", english: "Reading", pronunciation: "kee-yuh-vuh-nuh-vaa", image: "📚" },
    { sinhala: "ගායනා කරනවා", english: "Singing", pronunciation: "gaa-yuh-naa kuh-ruh-nuh-vaa", image: "🎤" },
    { sinhala: "නර්තනය කරනවා", english: "Dancing", pronunciation: "nur-thuh-nuh-yuh kuh-ruh-nuh-vaa", image: "💃" },
    { sinhala: "පිහිනනවා", english: "Swimming", pronunciation: "pee-hee-nuh-nuh-vaa", image: "🏊" },
    { sinhala: "ඇදගන්නවා", english: "Drawing", pronunciation: "eh-duh-guhn-nuh-vaa", image: "🎨" },
    { sinhala: "සෙල්ලම් කරනවා", english: "Playing", pronunciation: "sehl-luhm kuh-ruh-nuh-vaa", image: "⚽" },
    { sinhala: "උයනවා", english: "Cooking", pronunciation: "oo-yuh-nuh-vaa", image: "👨‍🍳" },
    { sinhala: "සෝදනවා", english: "Washing", pronunciation: "soh-duh-nuh-vaa", image: "🧼" },
    { sinhala: "අදිනවා", english: "Wearing", pronunciation: "uh-dee-nuh-vaa", image: "👔" },
  ];

  // Professions
  const professions = [
    { sinhala: "ගුරුවරයා", english: "Teacher (male)", pronunciation: "goo-roo-vuh-ruh-yaa", image: "👨‍🏫" },
    { sinhala: "ගුරුවරිය", english: "Teacher (female)", pronunciation: "goo-roo-vuh-ree-yuh", image: "👩‍🏫" },
    { sinhala: "වෛද්‍යවරයා", english: "Doctor", pronunciation: "vai-dhyuh-vuh-ruh-yaa", image: "👨‍⚕️" },
    { sinhala: "හෙදිය", english: "Nurse", pronunciation: "heh-dee-yuh", image: "👩‍⚕️" },
    { sinhala: "ඉංජිනේරු", english: "Engineer", pronunciation: "in-ji-nay-roo", image: "👷" },
    { sinhala: "නීතිඥ", english: "Lawyer", pronunciation: "nee-thig-nyuh", image: "⚖️" },
    { sinhala: "ගොවියා", english: "Farmer", pronunciation: "goh-vee-yaa", image: "👨‍🌾" },
    { sinhala: "ධීවරයා", english: "Fisherman", pronunciation: "dhee-vuh-ruh-yaa", image: "🎣" },
    { sinhala: "පොලිස් නිලධාරී", english: "Police Officer", pronunciation: "poh-lis nee-luh-dhaa-ree", image: "👮" },
    { sinhala: "ගුවන් සේවිකාව", english: "Flight Attendant", pronunciation: "goo-vuhn say-vee-kaa-vuh", image: "👩‍✈️" },
    { sinhala: "රියදුරා", english: "Driver", pronunciation: "ree-yuh-doo-raa", image: "🚗" },
    { sinhala: "බේකරු", english: "Baker", pronunciation: "bay-kuh-roo", image: "👨‍🍳" },
  ];

  // Vehicles
  const vehicles = [
    { sinhala: "මෝටර් රථය", english: "Car", pronunciation: "moh-tur ruh-thuh-yuh", image: "🚗" },
    { sinhala: "බස් රථය", english: "Bus", pronunciation: "bus ruh-thuh-yuh", image: "🚌" },
    { sinhala: "ත්‍රීවීලය", english: "Three-wheeler", pronunciation: "three-vee-luh-yuh", image: "🛺" },
    { sinhala: "යතුරුපැදිය", english: "Motorcycle", pronunciation: "yuh-thoo-roo-peh-dee-yuh", image: "🏍️" },
    { sinhala: "බයිසිකලය", english: "Bicycle", pronunciation: "by-si-kuh-luh-yuh", image: "🚲" },
    { sinhala: "දුම්රිය", english: "Train", pronunciation: "doom-ree-yuh", image: "🚆" },
    { sinhala: "ගුවන් යානය", english: "Airplane", pronunciation: "goo-vuhn yaa-nuh-yuh", image: "✈️" },
    { sinhala: "බෝට්ටුව", english: "Boat", pronunciation: "boht-too-vuh", image: "⛵" },
    { sinhala: "ලොරිය", english: "Truck", pronunciation: "loh-ree-yuh", image: "🚚" },
    { sinhala: "ටැක්සිය", english: "Taxi", pronunciation: "tek-see-yuh", image: "🚕" },
    { sinhala: "ඇම්බියුලන්ස්", english: "Ambulance", pronunciation: "em-bee-yoo-luhns", image: "🚑" },
    { sinhala: "ගිනි නිවන රථය", english: "Fire truck", pronunciation: "gee-nee ni-vuhn ruh-thuh-yuh", image: "🚒" },
  ];

  // Body parts
  const bodyParts = [
    { sinhala: "හිස", english: "Head", pronunciation: "hee-suh", image: "🗣️" },
    { sinhala: "ඇස", english: "Eye", pronunciation: "eh-suh", image: "👁️" },
    { sinhala: "කන", english: "Ear", pronunciation: "kuh-nuh", image: "👂" },
    { sinhala: "නාසය", english: "Nose", pronunciation: "naa-suh-yuh", image: "👃" },
    { sinhala: "කට", english: "Mouth", pronunciation: "kuh-tuh", image: "👄" },
    { sinhala: "අත", english: "Hand", pronunciation: "uh-thuh", image: "✋" },
    { sinhala: "පාදය", english: "Foot", pronunciation: "paa-duh-yuh", image: "🦶" },
    { sinhala: "කකුල", english: "Leg", pronunciation: "kuh-koo-luh", image: "🦵" },
    { sinhala: "බඩ", english: "Stomach", pronunciation: "buh-duh", image: "🫃" },
    { sinhala: "හදවත", english: "Heart", pronunciation: "huh-duh-vuh-thuh", image: "❤️" },
    { sinhala: "දත්", english: "Teeth", pronunciation: "duhth", image: "🦷" },
    { sinhala: "කෙස්", english: "Hair", pronunciation: "kehs", image: "💇" },
  ];

  // Weather
  const weather = [
    { sinhala: "අව්ව", english: "Sunshine", pronunciation: "uhv-vuh", image: "☀️" },
    { sinhala: "වැස්ස", english: "Rain", pronunciation: "vehs-suh", image: "🌧️" },
    { sinhala: "වළාකුළු", english: "Clouds", pronunciation: "vuh-laa-koo-loo", image: "☁️" },
    { sinhala: "සුළඟ", english: "Wind", pronunciation: "soo-luhn-guh", image: "💨" },
    { sinhala: "අකුණු", english: "Lightning", pronunciation: "uh-koo-noo", image: "⚡" },
    { sinhala: "ගිගුරුම්", english: "Thunder", pronunciation: "gee-goo-room", image: "🌩️" },
    { sinhala: "දේදුන්න", english: "Rainbow", pronunciation: "day-doon-nuh", image: "🌈" },
    { sinhala: "මීදුම", english: "Fog", pronunciation: "mee-doo-muh", image: "🌫️" },
    { sinhala: "හිම", english: "Snow", pronunciation: "hee-muh", image: "❄️" },
    { sinhala: "උණුසුම", english: "Hot weather", pronunciation: "oo-noo-soo-muh", image: "🥵" },
    { sinhala: "සීතල", english: "Cold weather", pronunciation: "see-thuh-luh", image: "🥶" },
    { sinhala: "කුණාටුව", english: "Storm", pronunciation: "koo-naa-too-vuh", image: "🌪️" },
  ];

  // Clothing
  const clothing = [
    { sinhala: "කමිසය", english: "Shirt", pronunciation: "kuh-mee-suh-yuh", image: "👔" },
    { sinhala: "කලිසම", english: "Trousers", pronunciation: "kuh-lee-suh-muh", image: "👖" },
    { sinhala: "සාය", english: "Skirt", pronunciation: "saa-yuh", image: "👗" },
    { sinhala: "ගවුම", english: "Dress", pronunciation: "gow-muh", image: "👗" },
    { sinhala: "ඇඳුම", english: "Clothes", pronunciation: "ehn-doo-muh", image: "👕" },
    { sinhala: "සපත්තු", english: "Shoes", pronunciation: "suh-puht-too", image: "👟" },
    { sinhala: "හිස් වැස්ම", english: "Hat", pronunciation: "his vehs-muh", image: "🎩" },
    { sinhala: "පටිය", english: "Belt", pronunciation: "puh-tee-yuh", image: "🥋" },
    { sinhala: "ජැකට්ටුව", english: "Jacket", pronunciation: "jeh-kuht-too-vuh", image: "🧥" },
    { sinhala: "රෙදිපිළි", english: "Garments", pronunciation: "reh-dee-pee-lee", image: "👚" },
    { sinhala: "ටී-ෂර්ට්", english: "T-shirt", pronunciation: "tee-shuht", image: "👕" },
    { sinhala: "සරම", english: "Sarong", pronunciation: "suh-ruh-muh", image: "🩱" },
  ];

  // Question words
  const questionWords = [
    { sinhala: "කවුද?", english: "Who?", pronunciation: "kow-duh" },
    { sinhala: "මොකද්ද?", english: "What?", pronunciation: "moh-kuhd-duh" },
    { sinhala: "කොහේද?", english: "Where?", pronunciation: "koh-hay-duh" },
    { sinhala: "කවදාද?", english: "When?", pronunciation: "kuh-vuh-daa-duh" },
    { sinhala: "ඇයි?", english: "Why?", pronunciation: "eh-yee" },
    { sinhala: "කොහොමද?", english: "How?", pronunciation: "koh-hoh-muh-duh" },
    { sinhala: "කීයද?", english: "How much?", pronunciation: "kee-yuh-duh" },
    { sinhala: "කී දෙනෙක්ද?", english: "How many?", pronunciation: "kee deh-neh-kuh-duh" },
  ];

  // Time expressions
  const timeExpressions = [
    { sinhala: "අද", english: "Today", pronunciation: "uh-duh" },
    { sinhala: "හෙට", english: "Tomorrow", pronunciation: "heh-tuh" },
    { sinhala: "ඊයේ", english: "Yesterday", pronunciation: "ee-yay" },
    { sinhala: "දැන්", english: "Now", pronunciation: "dehn" },
    { sinhala: "පසුව", english: "Later", pronunciation: "puh-soo-vuh" },
    { sinhala: "කලින්", english: "Before/Earlier", pronunciation: "kuh-lin" },
    { sinhala: "උදේ", english: "Morning", pronunciation: "oo-day" },
    { sinhala: "දවල්", english: "Afternoon", pronunciation: "duh-vuhl" },
    { sinhala: "හවස", english: "Evening", pronunciation: "huh-vuh-suh" },
    { sinhala: "රාත්‍රිය", english: "Night", pronunciation: "raath-ree-yuh" },
  ];

  // Basic conversations
  const conversations = [
    { sinhala: "ඔබට කොහොමද?", english: "How are you?", pronunciation: "oh-buh-tuh koh-hoh-muh-duh" },
    { sinhala: "මට හොඳින්.", english: "I am fine.", pronunciation: "muh-tuh hohn-deen" },
    { sinhala: "ඔබේ නම මොකද්ද?", english: "What is your name?", pronunciation: "oh-bay nuh-muh moh-kuhd-duh" },
    { sinhala: "මගේ නම සුනිල්.", english: "My name is Sunil.", pronunciation: "muh-gay nuh-muh soo-neel" },
    { sinhala: "කරුණාකර මට උදව් කරන්න.", english: "Please help me.", pronunciation: "kuh-roo-naa-kuh-ruh muh-tuh oo-duhv kuh-ruhn-nuh" },
    { sinhala: "ඔබ කොහෙන්ද එන්නේ?", english: "Where are you from?", pronunciation: "oh-buh koh-hehn-duh ehn-nay" },
    { sinhala: "මම කොළඹ ඉදන් එනවා.", english: "I come from Colombo.", pronunciation: "muh-muh koh-luhm-buh ee-duhn eh-nuh-vaa" },
  ];

  // Advanced conversations
  const advancedConversations = [
    {
      title: "At the Shop - වෙළෙඳසැලේදී",
      dialogue: [
        { speaker: "Customer", sinhala: "මේකේ ගාන කීයද?", english: "How much is this?", pronunciation: "may-kay gaa-nuh kee-yuh-duh" },
        { speaker: "Shopkeeper", sinhala: "එකේ ගාන රුපියල් සියයි.", english: "It's one hundred rupees.", pronunciation: "eh-kay gaa-nuh roo-pee-yuhl see-yuh-yee" },
        { speaker: "Customer", sinhala: "හරි, මම මේක ගන්නම්.", english: "Okay, I'll take this.", pronunciation: "huh-ree, muh-muh may-kuh guhn-nuhm" },
        { speaker: "Shopkeeper", sinhala: "ස්තූතියි, ආයෙත් එන්න.", english: "Thank you, come again.", pronunciation: "sthoo-thee-yee, aa-yeth ehn-nuh" },
      ]
    },
    {
      title: "Asking Directions - මාර්ගය අහනවා",
      dialogue: [
        { speaker: "Person A", sinhala: "සමාවන්න, බැංකුව කොහේද?", english: "Excuse me, where is the bank?", pronunciation: "suh-maa-vuhn-nuh, ben-koo-vuh koh-hay-duh" },
        { speaker: "Person B", sinhala: "මේ පාරේ කෙලින් යන්න.", english: "Go straight on this road.", pronunciation: "may paa-ray keh-lin yuhn-nuh" },
        { speaker: "Person B", sinhala: "ඊළඟ හන්දියේ වමට හැරෙන්න.", english: "Turn left at the next junction.", pronunciation: "ee-luh-nguh huhn-dee-yay vuh-muh-tuh heh-rehn-nuh" },
        { speaker: "Person A", sinhala: "ගොඩක් ස්තූතියි!", english: "Thank you very much!", pronunciation: "goh-duhk sthoo-thee-yee" },
      ]
    },
    {
      title: "At School - පාසලේදී",
      dialogue: [
        { speaker: "Teacher", sinhala: "හොඳ උදේක් ළමයි!", english: "Good morning children!", pronunciation: "hohn-duh oo-dayk luh-muh-yee" },
        { speaker: "Students", sinhala: "හොඳ උදේක් මහත්මිය!", english: "Good morning madam!", pronunciation: "hohn-duh oo-dayk muh-huht-mee-yuh" },
        { speaker: "Teacher", sinhala: "අද අපි ගණිතය ඉගෙන ගන්නවා.", english: "Today we will learn mathematics.", pronunciation: "uh-duh uh-pee guh-nee-thuh-yuh ee-geh-nuh guhn-nuh-vaa" },
        { speaker: "Student", sinhala: "මහත්මිය, මට ප්‍රශ්නයක් තියෙනවා.", english: "Madam, I have a question.", pronunciation: "muh-huht-mee-yuh, muh-tuh pruhsh-nuh-yuhk thee-yeh-nuh-vaa" },
      ]
    },
  ];

  // Travel phrases for common situations
  const travelPhrases = [
    { sinhala: "මට උදව් කරන්න පුළුවන්ද?", english: "Can you help me?", pronunciation: "muh-tuh oo-duhv kuh-ruhn-nuh poo-loo-vuhn-duh", category: "general" },
    { sinhala: "මම මග හැරුණා.", english: "I am lost.", pronunciation: "muh-muh muh-guh heh-roo-naa", category: "general" },
    { sinhala: "ඉංග්‍රීසි කතා කරනවාද?", english: "Do you speak English?", pronunciation: "in-gree-see kuh-thaa kuh-ruh-nuh-vaa-duh", category: "general" },
    { sinhala: "මේක කීයද?", english: "How much is this?", pronunciation: "may-kuh kee-yuh-duh", category: "shopping" },
    { sinhala: "ටිකක් අඩු කරන්න පුළුවන්ද?", english: "Can you reduce the price?", pronunciation: "tee-kuhk uh-doo kuh-ruhn-nuh poo-loo-vuhn-duh", category: "shopping" },
    { sinhala: "මම මේක ගන්නම්.", english: "I will take this.", pronunciation: "muh-muh may-kuh guhn-nuhm", category: "shopping" },
    { sinhala: "බස් නැවතුම කොහේද?", english: "Where is the bus stop?", pronunciation: "bus neh-vuh-thoo-muh koh-hay-duh", category: "transport" },
    { sinhala: "දුම්රිය ස්ථානය කොහේද?", english: "Where is the railway station?", pronunciation: "doom-ree-yuh sthaa-nuh-yuh koh-hay-duh", category: "transport" },
    { sinhala: "කොළඹට යන බස් එක මොකක්ද?", english: "Which bus goes to Colombo?", pronunciation: "koh-luhm-buh-tuh yuh-nuh bus eh-kuh moh-kuhk-duh", category: "transport" },
    { sinhala: "ටිකට් එකක් දෙන්න.", english: "Give me one ticket.", pronunciation: "tee-kuht eh-kuhk dehn-nuh", category: "transport" },
    { sinhala: "මේ බස් එක කොහේට යනවාද?", english: "Where does this bus go?", pronunciation: "may bus eh-kuh koh-hay-tuh yuh-nuh-vaa-duh", category: "transport" },
    { sinhala: "හොටෙල් එක කොහේද?", english: "Where is the hotel?", pronunciation: "hoh-tehl eh-kuh koh-hay-duh", category: "accommodation" },
    { sinhala: "කාමරයක් තියෙනවාද?", english: "Is there a room available?", pronunciation: "kaa-muh-ruh-yuhk thee-yeh-nuh-vaa-duh", category: "accommodation" },
    { sinhala: "එක රැයකට කීයද?", english: "How much per night?", pronunciation: "eh-kuh reh-yuh-kuh-tuh kee-yuh-duh", category: "accommodation" },
    { sinhala: "මට ආහාර ලැයිස්තුව දෙන්න.", english: "Give me the menu.", pronunciation: "muh-tuh aa-haa-ruh lay-is-thoo-vuh dehn-nuh", category: "food" },
    { sinhala: "මට වතුර ඕනේ.", english: "I need water.", pronunciation: "muh-tuh vuh-thoo-ruh oh-nay", category: "food" },
    { sinhala: "බිල එන්න.", english: "Bring the bill.", pronunciation: "bee-luh ehn-nuh", category: "food" },
    { sinhala: "මේක ගොඩක් රසයි!", english: "This is very tasty!", pronunciation: "may-kuh goh-duhk ruh-suh-yee", category: "food" },
    { sinhala: "වැසිකිළිය කොහේද?", english: "Where is the toilet?", pronunciation: "veh-see-kee-lee-yuh koh-hay-duh", category: "general" },
    { sinhala: "මට අසනීපයි.", english: "I am sick.", pronunciation: "muh-tuh uh-suh-nee-puh-yee", category: "emergency" },
    { sinhala: "රෝහල කොහේද?", english: "Where is the hospital?", pronunciation: "roh-huh-luh koh-hay-duh", category: "emergency" },
    { sinhala: "පොලිසියට කතා කරන්න.", english: "Call the police.", pronunciation: "poh-lee-see-yuh-tuh kuh-thaa kuh-ruhn-nuh", category: "emergency" },
    { sinhala: "කෙලින් යන්න.", english: "Go straight.", pronunciation: "keh-lin yuhn-nuh", category: "directions" },
    { sinhala: "වමට හැරෙන්න.", english: "Turn left.", pronunciation: "vuh-muh-tuh heh-rehn-nuh", category: "directions" },
    { sinhala: "දකුණට හැරෙන්න.", english: "Turn right.", pronunciation: "duh-koo-nuh-tuh heh-rehn-nuh", category: "directions" },
    { sinhala: "කොච්චර දුරද?", english: "How far is it?", pronunciation: "kohch-chuh-ruh doo-ruh-duh", category: "directions" },
    { sinhala: "ආයුබෝවන්!", english: "Welcome! / Hello!", pronunciation: "aa-yoo-boh-vuhn", category: "greetings" },
    { sinhala: "ස්තූතියි.", english: "Thank you.", pronunciation: "sthoo-thee-yee", category: "greetings" },
    { sinhala: "සමාවන්න.", english: "Sorry. / Excuse me.", pronunciation: "suh-maa-vuhn-nuh", category: "greetings" },
  ];

  // Travel dialogues
  const travelDialogues = [
    {
      title: "At the Airport - ගුවන් තොටුපළේදී",
      dialogue: [
        { speaker: "Traveler", sinhala: "කොළඹට යන ගේට් එක කොහේද?", english: "Where is the gate for Colombo?", pronunciation: "koh-luhm-buh-tuh yuh-nuh gate eh-kuh koh-hay-duh" },
        { speaker: "Staff", sinhala: "ගේට් එක 5. මේ පැත්තට යන්න.", english: "Gate 5. Go this way.", pronunciation: "gate eh-kuh 5. may peht-tuh-tuh yuhn-nuh" },
        { speaker: "Traveler", sinhala: "ගොඩක් ස්තූතියි!", english: "Thank you very much!", pronunciation: "goh-duhk sthoo-thee-yee" },
      ]
    },
    {
      title: "At a Restaurant - ආපනශාලාවේදී",
      dialogue: [
        { speaker: "Waiter", sinhala: "ආයුබෝවන්! මොනවද ඕනේ?", english: "Welcome! What would you like?", pronunciation: "aa-yoo-boh-vuhn! moh-nuh-vuh-duh oh-nay" },
        { speaker: "Customer", sinhala: "මට බත් සහ කරි ඕනේ.", english: "I want rice and curry.", pronunciation: "muh-tuh buhth suh-huh kuh-ree oh-nay" },
        { speaker: "Waiter", sinhala: "බොන්න මොනවද?", english: "What to drink?", pronunciation: "bohn-nuh moh-nuh-vuh-duh" },
        { speaker: "Customer", sinhala: "තේ එකක් දෙන්න.", english: "Give me a tea.", pronunciation: "thay eh-kuhk dehn-nuh" },
      ]
    },
    {
      title: "Taking a Tuk-Tuk - ත්‍රීවීලයක් ගන්නවා",
      dialogue: [
        { speaker: "Passenger", sinhala: "ගාල්ලට යනවාද?", english: "Do you go to Galle?", pronunciation: "gaal-luh-tuh yuh-nuh-vaa-duh" },
        { speaker: "Driver", sinhala: "ඔව්, යනවා. කීයද දෙන්නේ?", english: "Yes, I go. How much will you pay?", pronunciation: "ohv, yuh-nuh-vaa. kee-yuh-duh dehn-nay" },
        { speaker: "Passenger", sinhala: "රුපියල් පන්සියයක් හරිද?", english: "Is 500 rupees okay?", pronunciation: "roo-pee-yuhl puhn-see-yuh-yuhk huh-ree-duh" },
        { speaker: "Driver", sinhala: "හරි, නගින්න.", english: "Okay, get in.", pronunciation: "huh-ree, nuh-gin-nuh" },
      ]
    },
    {
      title: "At a Hotel - හෝටලයේදී",
      dialogue: [
        { speaker: "Guest", sinhala: "කාමරයක් තියෙනවාද?", english: "Is there a room available?", pronunciation: "kaa-muh-ruh-yuhk thee-yeh-nuh-vaa-duh" },
        { speaker: "Receptionist", sinhala: "ඔව්, තියෙනවා. කී දවසකටද?", english: "Yes, there is. For how many days?", pronunciation: "ohv, thee-yeh-nuh-vaa. kee duh-vuh-suh-kuh-tuh-duh" },
        { speaker: "Guest", sinhala: "දවස් දෙකකට.", english: "For two days.", pronunciation: "duh-vuhs deh-kuh-kuh-tuh" },
        { speaker: "Receptionist", sinhala: "හරි. පාස්පෝට් එක දෙන්න.", english: "Okay. Give me your passport.", pronunciation: "huh-ree. paas-poht eh-kuh dehn-nuh" },
      ]
    },
  ];

  // Quiz questions - expanded to 20
  const quizQuestions = [
    {
      question: "What is the word order in Sinhala?",
      options: ["SVO", "SOV", "VSO", "OSV"],
      correct: 1,
    },
    {
      question: 'What does "පාසල" mean?',
      options: ["Hospital", "Home", "School", "Shop"],
      correct: 2,
    },
    {
      question: "Which suffix makes present tense?",
      options: ["-ුවා", "-නවා", "-ා", "-ම්"],
      correct: 1,
    },
    {
      question: 'What does "ඉගෙන ගන්නවා" mean?',
      options: ["Teaching", "Learning", "Playing", "Working"],
      correct: 1,
    },
    {
      question: 'How do you ask "What is your name?"',
      options: ["ඔබට කොහොමද?", "ඔබේ නම මොකද්ද?", "ඔබ කොහේද?", "ඔබ කවුද?"],
      correct: 1,
    },
    {
      question: 'What place is "පුස්තකාලය"?',
      options: ["Temple", "Library", "Hospital", "Garden"],
      correct: 1,
    },
    {
      question: 'What does "රෝහල" mean?',
      options: ["School", "Hospital", "Shop", "Temple"],
      correct: 1,
    },
    {
      question: 'How do you say "I am fine" in Sinhala?',
      options: ["මම යනවා", "මට හොඳින්", "මම කනවා", "මම ඉන්නවා"],
      correct: 1,
    },
    {
      question: 'What does "දුවනවා" mean?',
      options: ["Walking", "Running", "Sitting", "Standing"],
      correct: 1,
    },
    {
      question: 'Which place is "වෙළෙඳසැල"?',
      options: ["School", "Hospital", "Shop", "Garden"],
      correct: 2,
    },
    {
      question: 'What does "ගුරුවරයා" mean?',
      options: ["Doctor", "Teacher", "Farmer", "Engineer"],
      correct: 1,
    },
    {
      question: 'How do you say "yesterday" in Sinhala?',
      options: ["අද", "හෙට", "ඊයේ", "දැන්"],
      correct: 2,
    },
    {
      question: 'What is "කමිසය" in English?',
      options: ["Trousers", "Shirt", "Hat", "Shoes"],
      correct: 1,
    },
    {
      question: 'Which question word means "Where?"',
      options: ["කවුද?", "මොකද්ද?", "කොහේද?", "ඇයි?"],
      correct: 2,
    },
    {
      question: 'What does "වෛද්‍යවරයා" mean?',
      options: ["Teacher", "Nurse", "Doctor", "Lawyer"],
      correct: 2,
    },
    {
      question: 'How do you say "tomorrow" in Sinhala?',
      options: ["ඊයේ", "අද", "හෙට", "පසුව"],
      correct: 2,
    },
    {
      question: "Which suffix makes past tense?",
      options: ["-නවා", "-වා / -ුවා", "-යි", "-න්න"],
      correct: 1,
    },
    {
      question: 'What is "සපත්තු" in English?',
      options: ["Hat", "Belt", "Shoes", "Jacket"],
      correct: 2,
    },
    {
      question: 'What does "ගොවියා" mean?',
      options: ["Fisherman", "Driver", "Baker", "Farmer"],
      correct: 3,
    },
    {
      question: 'How do you ask "How much?" in Sinhala?',
      options: ["කවුද?", "කීයද?", "ඇයි?", "කොහොමද?"],
      correct: 1,
    },
  ];

  const renderSection = () => {
    switch (currentSection) {
      case "grammar":
        return (
          <section className="section active">
            <h2>Basic Grammar - මූලික ව්‍යාකරණ</h2>
            <div className="grammar-box">
              <h4>Word Order - වචන පිළිවෙල</h4>
              <p>Sinhala follows Subject-Object-Verb (SOV) word order:</p>
              <p>
                <strong>
                  මම (I) + බත් (rice) + කනවා (eat) = මම බත් කනවා (I eat rice)
                </strong>
              </p>
            </div>
            <div className="grammar-box">
              <h4>Present Tense - වර්තමාන කාලය</h4>
              <p>Add -නවා (-nawa) to the verb stem:</p>
              <p><strong>කන (eat) + නවා = කනවා (eating/eat)</strong></p>
              <p><strong>යන (go) + නවා = යනවා (going/go)</strong></p>
              <p><strong>බොන (drink) + නවා = බොනවා (drinking/drink)</strong></p>
            </div>
            <div className="grammar-box">
              <h4>Plural Forms - බහුවචන</h4>
              <p>Add -ලා (-la) or -න් (-n) to make plurals:</p>
              <p><strong>ළමයා (child) → ළමයින් (children)</strong></p>
              <p><strong>පොත (book) → පොත් (books)</strong></p>
              <p><strong>ගස (tree) → ගස් (trees)</strong></p>
              <p><strong>කුරුල්ලා (bird) → කුරුල්ලන් (birds)</strong></p>
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              Question Words - ප්‍රශ්න වචන
            </h3>
            <div className="word-grid">
              {questionWords.map((word, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={word.sinhala}
                  englishWord={word.english}
                  pronunciation={word.pronunciation}
                />
              ))}
            </div>

            <div className="activity-card">
              <h3>📝 Practice Exercise</h3>
              <p>Form questions using these question words:</p>
              <p><strong>1. ඔබේ නම ___? (What is your name?)</strong> → ඔබේ නම මොකද්ද?</p>
              <p><strong>2. ඔබ ___ යනවා? (Where are you going?)</strong> → ඔබ කොහේද යනවා?</p>
              <p><strong>3. ඔබ ___ එන්නේ? (When are you coming?)</strong> → ඔබ කවදාද එන්නේ?</p>
            </div>
          </section>
        );

      case "tenses":
        return (
          <section className="section active">
            <h2>Verb Tenses - ක්‍රියා පද කාල</h2>

            <div className="grammar-box">
              <h4>Present Tense - වර්තමාන කාලය</h4>
              <p>Use -නවා (-nawa) ending:</p>
              <table className="tense-table">
                <thead>
                  <tr>
                    <th>Sinhala</th>
                    <th>English</th>
                    <th>Pronunciation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>මම කනවා</td><td>I eat</td><td>muh-muh kuh-nuh-vaa</td></tr>
                  <tr><td>ඔහු යනවා</td><td>He goes</td><td>oh-hoo yuh-nuh-vaa</td></tr>
                  <tr><td>ඇය ලියනවා</td><td>She writes</td><td>eh-yuh lee-yuh-nuh-vaa</td></tr>
                  <tr><td>අපි ඉගෙන ගන්නවා</td><td>We learn</td><td>uh-pee ee-geh-nuh guhn-nuh-vaa</td></tr>
                </tbody>
              </table>
            </div>

            <div className="grammar-box">
              <h4>Past Tense - අතීත කාලය</h4>
              <p>Use -වා (-wa) or -ුවා (-uwa) ending:</p>
              <table className="tense-table">
                <thead>
                  <tr>
                    <th>Sinhala</th>
                    <th>English</th>
                    <th>Pronunciation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>මම කෑවා</td><td>I ate</td><td>muh-muh keh-vaa</td></tr>
                  <tr><td>ඔහු ගියා</td><td>He went</td><td>oh-hoo gee-yaa</td></tr>
                  <tr><td>ඇය ලිව්වා</td><td>She wrote</td><td>eh-yuh liv-vaa</td></tr>
                  <tr><td>අපි ඉගෙන ගත්තා</td><td>We learned</td><td>uh-pee ee-geh-nuh guht-taa</td></tr>
                </tbody>
              </table>
            </div>

            <div className="grammar-box">
              <h4>Future Tense - අනාගත කාලය</h4>
              <p>Use -න්නම් (-nnam), -යි (-yi), or -වි (-wi) ending:</p>
              <table className="tense-table">
                <thead>
                  <tr>
                    <th>Sinhala</th>
                    <th>English</th>
                    <th>Pronunciation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>මම කන්නම්</td><td>I will eat</td><td>muh-muh kuhn-nuhm</td></tr>
                  <tr><td>ඔහු යනවා</td><td>He will go</td><td>oh-hoo yuh-nuh-vaa</td></tr>
                  <tr><td>ඇය ලියන්නම්</td><td>She will write</td><td>eh-yuh lee-yuhn-nuhm</td></tr>
                  <tr><td>අපි එන්නම්</td><td>We will come</td><td>uh-pee ehn-nuhm</td></tr>
                </tbody>
              </table>
            </div>

            <div className="grammar-box">
              <h4>Negation - නිෂේධය</h4>
              <p>Add "නෑ" (neh) at the end or use negative verb forms:</p>
              <table className="tense-table">
                <thead>
                  <tr>
                    <th>Positive</th>
                    <th>Negative</th>
                    <th>English</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>මම කනවා</td><td>මම කන්නේ නෑ</td><td>I don't eat</td></tr>
                  <tr><td>ඔහු යනවා</td><td>ඔහු යන්නේ නෑ</td><td>He doesn't go</td></tr>
                  <tr><td>මම කෑවා</td><td>මම කෑවේ නෑ</td><td>I didn't eat</td></tr>
                  <tr><td>ඔහු ගියා</td><td>ඔහු ගියේ නෑ</td><td>He didn't go</td></tr>
                </tbody>
              </table>
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              Time Expressions - කාල ප්‍රකාශන
            </h3>
            <div className="word-grid">
              {timeExpressions.map((word, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={word.sinhala}
                  englishWord={word.english}
                  pronunciation={word.pronunciation}
                />
              ))}
            </div>

            <div className="activity-card">
              <h3>📝 Tense Practice</h3>
              <p>Fill in the correct tense:</p>
              <p><strong>1. ඊයේ මම පාසලට ___ (go - past)</strong> → ගියා</p>
              <p><strong>2. හෙට මම ක්‍රිකට් ___ (play - future)</strong> → ගහන්නම්</p>
              <p><strong>3. දැන් මම TV ___ (watch - present)</strong> → බලනවා</p>
            </div>
          </section>
        );

      case "conversation":
        return (
          <section className="section active">
            <h2>Everyday Conversation - දෛනික සංවාදය</h2>
            <h3 style={{ color: "#667eea", marginBottom: "20px", textAlign: "center" }}>
              Basic Questions & Answers
            </h3>
            {conversations.map((conv, index) => (
              <div key={index} className="sentence-practice">
                <div className="sentence-sinhala">{conv.sinhala}</div>
                <ReadAloudButton text={conv.sinhala} />
                <div className="sentence-english">{conv.english}</div>
                <div className="sentence-pronunciation">{conv.pronunciation}</div>
              </div>
            ))}

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              Situational Dialogues - තත්ත්ව සංවාද
            </h3>
            {advancedConversations.map((conv, index) => (
              <div key={index} className="story-card" style={{ marginBottom: "20px" }}>
                <h3>💬 {conv.title}</h3>
                {conv.dialogue.map((line, lineIndex) => (
                  <div key={lineIndex} className="dialogue-line" style={{
                    padding: "10px",
                    margin: "10px 0",
                    backgroundColor: lineIndex % 2 === 0 ? "#f0f4ff" : "#fff0f5",
                    borderRadius: "8px"
                  }}>
                    <strong>{line.speaker}:</strong>
                    <div className="sentence-sinhala" style={{ fontSize: "1.1em" }}>{line.sinhala}</div>
                    <ReadAloudButton text={line.sinhala} />
                    <div className="sentence-english">{line.english}</div>
                    <div className="sentence-pronunciation" style={{ fontSize: "0.9em", color: "#888" }}>
                      {line.pronunciation}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div className="activity-card">
              <h3>💬 Conversation Practice</h3>
              <p>Practice these conversations with a friend or family member!</p>
              <p><strong>Challenge:</strong> Can you create your own conversation using these phrases?</p>
            </div>
          </section>
        );

      case "travel":
        return (
          <section className="section active">
            <h2>Travel Phrases - සංචාරක වාක්‍ය</h2>
            <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>
              Essential phrases for traveling in Sri Lanka ✈️🇱🇰
            </p>

            <h3 style={{ color: "#667eea", marginBottom: "20px", textAlign: "center" }}>
              🗣️ General Phrases - පොදු වාක්‍ය
            </h3>
            <div className="word-grid">
              {travelPhrases.filter(p => p.category === "general" || p.category === "greetings").map((phrase, index) => (
                <div key={index} className="sentence-practice" style={{ margin: "10px" }}>
                  <div className="sentence-sinhala">{phrase.sinhala}</div>
                  <ReadAloudButton text={phrase.sinhala} />
                  <div className="sentence-english">{phrase.english}</div>
                  <div className="sentence-pronunciation">{phrase.pronunciation}</div>
                </div>
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              🚌 Transport - ප්‍රවාහනය
            </h3>
            <div className="word-grid">
              {travelPhrases.filter(p => p.category === "transport").map((phrase, index) => (
                <div key={index} className="sentence-practice" style={{ margin: "10px" }}>
                  <div className="sentence-sinhala">{phrase.sinhala}</div>
                  <ReadAloudButton text={phrase.sinhala} />
                  <div className="sentence-english">{phrase.english}</div>
                  <div className="sentence-pronunciation">{phrase.pronunciation}</div>
                </div>
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              🧭 Directions - දිශාවන්
            </h3>
            <div className="word-grid">
              {travelPhrases.filter(p => p.category === "directions").map((phrase, index) => (
                <div key={index} className="sentence-practice" style={{ margin: "10px" }}>
                  <div className="sentence-sinhala">{phrase.sinhala}</div>
                  <ReadAloudButton text={phrase.sinhala} />
                  <div className="sentence-english">{phrase.english}</div>
                  <div className="sentence-pronunciation">{phrase.pronunciation}</div>
                </div>
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              🛒 Shopping - සාප්පු සවාරි
            </h3>
            <div className="word-grid">
              {travelPhrases.filter(p => p.category === "shopping").map((phrase, index) => (
                <div key={index} className="sentence-practice" style={{ margin: "10px" }}>
                  <div className="sentence-sinhala">{phrase.sinhala}</div>
                  <ReadAloudButton text={phrase.sinhala} />
                  <div className="sentence-english">{phrase.english}</div>
                  <div className="sentence-pronunciation">{phrase.pronunciation}</div>
                </div>
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              🍛 Food & Dining - ආහාර සහ අවන්හල්
            </h3>
            <div className="word-grid">
              {travelPhrases.filter(p => p.category === "food").map((phrase, index) => (
                <div key={index} className="sentence-practice" style={{ margin: "10px" }}>
                  <div className="sentence-sinhala">{phrase.sinhala}</div>
                  <ReadAloudButton text={phrase.sinhala} />
                  <div className="sentence-english">{phrase.english}</div>
                  <div className="sentence-pronunciation">{phrase.pronunciation}</div>
                </div>
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              🏨 Accommodation - නවාතැන්
            </h3>
            <div className="word-grid">
              {travelPhrases.filter(p => p.category === "accommodation").map((phrase, index) => (
                <div key={index} className="sentence-practice" style={{ margin: "10px" }}>
                  <div className="sentence-sinhala">{phrase.sinhala}</div>
                  <ReadAloudButton text={phrase.sinhala} />
                  <div className="sentence-english">{phrase.english}</div>
                  <div className="sentence-pronunciation">{phrase.pronunciation}</div>
                </div>
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              🚨 Emergency - හදිසි අවස්ථා
            </h3>
            <div className="word-grid">
              {travelPhrases.filter(p => p.category === "emergency").map((phrase, index) => (
                <div key={index} className="sentence-practice" style={{ margin: "10px" }}>
                  <div className="sentence-sinhala">{phrase.sinhala}</div>
                  <ReadAloudButton text={phrase.sinhala} />
                  <div className="sentence-english">{phrase.english}</div>
                  <div className="sentence-pronunciation">{phrase.pronunciation}</div>
                </div>
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              💬 Travel Dialogues - සංචාරක සංවාද
            </h3>
            {travelDialogues.map((conv, index) => (
              <div key={index} className="story-card" style={{ marginBottom: "20px" }}>
                <h3>🗨️ {conv.title}</h3>
                {conv.dialogue.map((line, lineIndex) => (
                  <div key={lineIndex} className="dialogue-line" style={{
                    padding: "10px",
                    margin: "10px 0",
                    backgroundColor: lineIndex % 2 === 0 ? "#e8f4f8" : "#fff5e6",
                    borderRadius: "8px"
                  }}>
                    <strong>{line.speaker}:</strong>
                    <div className="sentence-sinhala" style={{ fontSize: "1.1em" }}>{line.sinhala}</div>
                    <ReadAloudButton text={line.sinhala} />
                    <div className="sentence-english">{line.english}</div>
                    <div className="sentence-pronunciation" style={{ fontSize: "0.9em", color: "#888" }}>
                      {line.pronunciation}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div className="activity-card">
              <h3>🎯 Travel Practice</h3>
              <p>Practice these scenarios:</p>
              <p><strong>1.</strong> Ask for directions to the train station</p>
              <p><strong>2.</strong> Order food at a restaurant</p>
              <p><strong>3.</strong> Negotiate a price with a tuk-tuk driver</p>
              <p><strong>4.</strong> Check into a hotel</p>
            </div>
          </section>
        );

      case "vocabulary":
        return (
          <section className="section active">
            <h2>Advanced Vocabulary - උසස් වචන</h2>
            <h3 style={{ color: "#667eea", marginBottom: "20px", textAlign: "center" }}>
              Places - ස්ථාන
            </h3>
            <div className="word-grid">
              {places.map((place, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={place.sinhala}
                  englishWord={place.english}
                  pronunciation={place.pronunciation}
                  image={place.image}
                />
              ))}
            </div>
            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              Actions - ක්‍රියා
            </h3>
            <div className="word-grid">
              {actions.map((action, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={action.sinhala}
                  englishWord={action.english}
                  pronunciation={action.pronunciation}
                  image={action.image}
                />
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              Vehicles - වාහන
            </h3>
            <div className="word-grid">
              {vehicles.map((vehicle, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={vehicle.sinhala}
                  englishWord={vehicle.english}
                  pronunciation={vehicle.pronunciation}
                  image={vehicle.image}
                />
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              Body Parts - ශරීර කොටස්
            </h3>
            <div className="word-grid">
              {bodyParts.map((part, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={part.sinhala}
                  englishWord={part.english}
                  pronunciation={part.pronunciation}
                  image={part.image}
                />
              ))}
            </div>

            <h3 style={{ color: "#667eea", margin: "30px 0 20px 0", textAlign: "center" }}>
              Weather - කාලගුණය
            </h3>
            <div className="word-grid">
              {weather.map((item, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={item.sinhala}
                  englishWord={item.english}
                  pronunciation={item.pronunciation}
                  image={item.image}
                />
              ))}
            </div>

            <div className="activity-card">
              <h3>🎯 Vocabulary Exercise</h3>
              <p>Make sentences using places and actions:</p>
              <p><strong>Example:</strong> මම පුස්තකාලයේ කියවනවා. (I am reading at the library.)</p>
              <p>1. I swim at the beach. → _____</p>
              <p>2. Children play at school. → _____</p>
              <p><strong>Answers:</strong></p>
              <p>1. මම වෙරළේ පිහිනනවා.</p>
              <p>2. ළමයින් පාසලේ සෙල්ලම් කරනවා.</p>
            </div>
          </section>
        );

      case "professions":
        return (
          <section className="section active">
            <h2>Professions - වෘත්තීන්</h2>
            <h3 style={{ color: "#667eea", marginBottom: "20px", textAlign: "center" }}>
              Jobs & Occupations - රැකියා
            </h3>
            <div className="word-grid">
              {professions.map((prof, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={prof.sinhala}
                  englishWord={prof.english}
                  pronunciation={prof.pronunciation}
                  image={prof.image}
                />
              ))}
            </div>

            <div className="grammar-box" style={{ marginTop: "30px" }}>
              <h4>Talking About Professions</h4>
              <p>Use "වෙනවා" (venawa) to say what someone is/becomes:</p>
              <p><strong>මම ගුරුවරයෙක් වෙනවා.</strong> (I am becoming a teacher.)</p>
              <p><strong>ඔහු වෛද්‍යවරයෙක්.</strong> (He is a doctor.)</p>
              <p><strong>ඇය හෙදියක්.</strong> (She is a nurse.)</p>
            </div>

            <div className="activity-card">
              <h3>💼 Career Talk</h3>
              <p>Answer these questions:</p>
              <p><strong>ඔබ මොනවාද වෙන්න කැමති?</strong> (What do you want to become?)</p>
              <p>Example answer: <strong>මම ඉංජිනේරුවෙක් වෙන්න කැමතියි.</strong></p>
              <p>(I want to become an engineer.)</p>
            </div>

            <div className="sentence-practice">
              <div className="sentence-sinhala">මගේ තාත්තා ගොවියෙක්. ඔහු ගොවිතැනේ වැඩ කරනවා.</div>
              <ReadAloudButton text="මගේ තාත්තා ගොවියෙක්. ඔහු ගොවිතැනේ වැඩ කරනවා." />
              <div className="sentence-english">My father is a farmer. He works in the farm.</div>
              <div className="sentence-pronunciation">Mage thaththa goviyek. Ohu govithane veda karanawa.</div>
            </div>

            <div className="sentence-practice">
              <div className="sentence-sinhala">මගේ අම්මා ගුරුවරියක්. ඇය පාසලේ ඉගැන්වනවා.</div>
              <ReadAloudButton text="මගේ අම්මා ගුරුවරියක්. ඇය පාසලේ ඉගැන්වනවා." />
              <div className="sentence-english">My mother is a teacher. She teaches at school.</div>
              <div className="sentence-pronunciation">Mage amma guruvariyak. Eya pasale igenvnawa.</div>
            </div>
          </section>
        );

      case "clothing":
        return (
          <section className="section active">
            <h2>Clothing - ඇඳුම් පැළඳුම්</h2>
            <h3 style={{ color: "#667eea", marginBottom: "20px", textAlign: "center" }}>
              Clothes & Accessories
            </h3>
            <div className="word-grid">
              {clothing.map((item, index) => (
                <WordCard
                  key={index}
                  sinhalaWord={item.sinhala}
                  englishWord={item.english}
                  pronunciation={item.pronunciation}
                  image={item.image}
                />
              ))}
            </div>

            <div className="grammar-box" style={{ marginTop: "30px" }}>
              <h4>Talking About Clothes</h4>
              <p>Use "අදිනවා" (adinawa) to say wearing:</p>
              <p><strong>මම නිල් කමිසයක් අදිනවා.</strong> (I am wearing a blue shirt.)</p>
              <p><strong>ඇය ලස්සන ගවුමක් අඳිනවා.</strong> (She is wearing a beautiful dress.)</p>
            </div>

            <div className="activity-card">
              <h3>👕 What are you wearing?</h3>
              <p><strong>ඔබ මොනවද අඳින්නේ?</strong> (What are you wearing?)</p>
              <p>Practice describing what you and others are wearing:</p>
              <p>Example: <strong>මම සුදු ටී-ෂර්ට් එකක් සහ නිල් කලිසමක් අඳිනවා.</strong></p>
              <p>(I am wearing a white T-shirt and blue trousers.)</p>
            </div>

            <div className="sentence-practice">
              <div className="sentence-sinhala">පාසල් යන්න මම නිල ඇඳුම අඳිනවා.</div>
              <ReadAloudButton text="පාසල් යන්න මම නිල ඇඳුම අඳිනවා." />
              <div className="sentence-english">I wear the school uniform to go to school.</div>
              <div className="sentence-pronunciation">Pasal yanna mama nila enduma adinawa.</div>
            </div>

            <div className="sentence-practice">
              <div className="sentence-sinhala">අද හිරු කරනවා. මම හිස් වැස්මක් අඳිනවා.</div>
              <ReadAloudButton text="අද හිරු කරනවා. මම හිස් වැස්මක් අඳිනවා." />
              <div className="sentence-english">It's sunny today. I am wearing a hat.</div>
              <div className="sentence-pronunciation">Ada hiru karanawa. Mama his vesmak adinawa.</div>
            </div>
          </section>
        );

      case "reading":
        return (
          <section className="section active">
            <h2>Reading Practice - කියවීමේ අභ්‍යාස</h2>
            <div className="reading-passage">
              <h3>📚 The School Day</h3>
              <div className="reading-sinhala">
                සුනිල් හොඳ ළමයෙක්. ඔහු දවසින් දවස පාසලට යනවා. පාසලේදී ඔහු ගණිතය, සිංහල, ඉංග්‍රීසි ඉගෙන ගන්නවා.
                ගුරුවරුන් ඔහුට ගොඩක් ආදරේයි. ඔහුගේ මිතුරන් ද ඔහුව ආදරේ කරනවා.
              </div>
              <ReadAloudButton
                text="සුනිල් හොඳ ළමයෙක්. ඔහු දවසින් දවස පාසලට යනවා. පාසලේදී ඔහු ගණිතය, සිංහල, ඉංග්‍රීසි ඉගෙන ගන්නවා. ගුරුවරුන් ඔහුට ගොඩක් ආදරේයි. ඔහුගේ මිතුරන් ද ඔහුව ආදරේ කරනවා."
                label="Listen in Sinhala"
                labelSinhala="සිංහලෙන් අසන්න"
              />
              <div className="reading-english">
                Sunil is a good boy. He goes to school every day. At school he learns mathematics, Sinhala, and English.
                The teachers love him very much. His friends also love him.
              </div>
            </div>

            <div className="reading-passage">
              <h3>🏡 The Village Market</h3>
              <div className="reading-sinhala">
                සෙනසුරාදා දවසේ අම්මා සහ මම කඩ යනවා. කඩේ ගොඩක් එළවළු, පළතුරු තියෙනවා.
                අම්මා මාළු, බත්, එළවළු ගන්නවා. මම කුකුළු මස් කාන්න ඕනේ. අපි සතුටින් ගෙදරට එනවා.
              </div>
              <ReadAloudButton
                text="සෙනසුරාදා දවසේ අම්මා සහ මම කඩ යනවා. කඩේ ගොඩක් එළවළු, පළතුරු තියෙනවා. අම්මා මාළු, බත්, එළවළු ගන්නවා. මම කුකුළු මස් කාන්න ඕනේ. අපි සතුටින් ගෙදරට එනවා."
                label="Listen in Sinhala"
                labelSinhala="සිංහලෙන් අසන්න"
              />
              <div className="reading-english">
                On Saturday mother and I go to the shop. There are many vegetables and fruits in the shop.
                Mother buys fish, rice, and vegetables. I want to eat chicken. We come home happily.
              </div>
            </div>

            <div className="reading-passage">
              <h3>🚌 The School Trip</h3>
              <div className="reading-sinhala">
                අපේ පාසලෙන් ඊයේ චාරිකාවකට ගියා. අපි සත්ව උද්‍යානයට ගියා. එහි අපි අලි, සිංහ, වඳුරන් දැක්කා.
                අලි ගොඩක් ලොකු! වඳුරන් ගස්වල පැන්නා. සිංහයා නිදාගෙන හිටියා. අපි ගොඩක් සතුටු වුණා.
                සන්දේ වෙලාවට අපි ආපසු ගෙදර ආවා.
              </div>
              <ReadAloudButton
                text="අපේ පාසලෙන් ඊයේ චාරිකාවකට ගියා. අපි සත්ව උද්‍යානයට ගියා. එහි අපි අලි, සිංහ, වඳුරන් දැක්කා. අලි ගොඩක් ලොකු! වඳුරන් ගස්වල පැන්නා. සිංහයා නිදාගෙන හිටියා. අපි ගොඩක් සතුටු වුණා. සන්දේ වෙලාවට අපි ආපසු ගෙදර ආවා."
                label="Listen in Sinhala"
                labelSinhala="සිංහලෙන් අසන්න"
              />
              <div className="reading-english">
                Our school went on a trip yesterday. We went to the zoo. There we saw elephants, lions, and monkeys.
                The elephants were very big! Monkeys jumped on trees. The lion was sleeping. We were very happy.
                In the evening we came back home.
              </div>
            </div>

            <div className="activity-card">
              <h3>📖 Reading Questions</h3>
              <p>Answer these questions about "The School Trip":</p>
              <p>1. Where did the school go?</p>
              <p>2. What animals did they see?</p>
              <p>3. What was the lion doing?</p>
              <p>4. How did the children feel?</p>
            </div>
          </section>
        );

      case "writing":
        return (
          <section className="section active">
            <h2>Writing Practice - ලිවීමේ අභ්‍යාස</h2>
            <div className="grammar-box">
              <h4>Sentence Building - වාක්‍ය සෑදීම</h4>
              <p>Practice writing sentences with Subject + Object + Verb:</p>
              <p>
                <strong>Example: ළමයා + පොත + කියවනවා = ළමයා පොත කියවනවා</strong>
              </p>
            </div>

            <div className="activity-card">
              <h3>✍️ Writing Exercise 1</h3>
              <p>Complete these sentences in Sinhala:</p>
              <p>1. I _____ to school. (go)</p>
              <p>2. Mother _____ rice. (cooks)</p>
              <p>3. Children _____ in the garden. (play)</p>
              <p><strong>Answers:</strong></p>
              <p>1. මම පාසලට යනවා.</p>
              <p>2. අම්මා බත් උයනවා.</p>
              <p>3. ළමයින් උයනේ සෙල්ලම් කරනවා.</p>
            </div>

            <div className="activity-card">
              <h3>✍️ Writing Exercise 2 - Past Tense</h3>
              <p>Convert these present tense sentences to past tense:</p>
              <p>1. මම කනවා → _____ (I ate)</p>
              <p>2. ඔහු යනවා → _____ (He went)</p>
              <p>3. ඇය ලියනවා → _____ (She wrote)</p>
              <p><strong>Answers:</strong></p>
              <p>1. මම කෑවා</p>
              <p>2. ඔහු ගියා</p>
              <p>3. ඇය ලිව්වා</p>
            </div>

            <div className="activity-card">
              <h3>✍️ Writing Exercise 3 - My Day</h3>
              <p>Write about your typical day using at least 5 sentences in Sinhala.</p>
              <p>Include: waking up, eating, going to school, activities, sleeping</p>
              <p><strong>Example:</strong></p>
              <div className="reading-sinhala" style={{ background: "#f5f5f5", padding: "10px", borderRadius: "8px" }}>
                මම උදේ 6 ට නැගිටිනවා. මුණ හෝදලා, උදේ ආහාර කනවා.
                7.30 ට පාසලට යනවා. පාසලේදී ඉගෙන ගන්නවා.
                දවල් ආහාර කනවා. හවසට ගෙදර එනවා.
                ගෙදර ඇවිත් සෙල්ලම් කරනවා. රාත්‍රි 9 ට නිදාගන්නවා.
              </div>
            </div>

            <div className="activity-card">
              <h3>✍️ Writing Exercise 4 - My Family</h3>
              <p>Write about your family using at least 3 sentences in Sinhala.</p>
              <p>Include: family members, what they do, where you live</p>
              <p><strong>Example:</strong></p>
              <p>මගේ පවුලේ හතර දෙනෙක් ඉන්නවා. අපි කොළඹ ජීවත් වෙනවා. තාත්තා වැඩට යනවා.</p>
            </div>
          </section>
        );

      case "stories":
        return (
          <section className="section active">
            <h2>Stories - කතා</h2>
            <div className="story-card">
              <h3>🌳 The Kind Tree</h3>
              <div className="reading-sinhala">
                ගමේ ලොකු ගසක් තිබුණා. ඒ ගස ගොඩක් මිනිස්සුන්ට උදව් කළා. හිරු කරන දවස්වල මිනිස්සු ගස යට හිටියා.
                වර්ෂාකාලයේ ගස ආරක්ෂාව දුන්නා. කුරුල්ලන්ට ගහේ කූඩු හදන්න ඉඩ දුන්නා. සියලුදෙනා ගසට ස්තූති කළා.
              </div>
              <ReadAloudButton
                text="ගමේ ලොකු ගසක් තිබුණා. ඒ ගස ගොඩක් මිනිස්සුන්ට උදව් කළා. හිරු කරන දවස්වල මිනිස්සු ගස යට හිටියා. වර්ෂාකාලයේ ගස ආරක්ෂාව දුන්නා. කුරුල්ලන්ට ගහේ කූඩු හදන්න ඉඩ දුන්නා. සියලුදෙනා ගසට ස්තූති කළා."
                label="Listen in Sinhala"
                labelSinhala="සිංහලෙන් අසන්න"
              />
              <div className="reading-english">
                There was a big tree in the village. That tree helped many people. On sunny days people stayed under the tree.
                In rainy season the tree gave protection. It let birds build nests on the tree. Everyone thanked the tree.
              </div>
            </div>

            <div className="story-card">
              <h3>🐰 The Clever Rabbit</h3>
              <div className="reading-sinhala">
                කුඩා හාවෙක් වනයේ ජීවත් වුණා. ඔහු ගොඩක් බුද්ධිමත්. දිනක් ලොකු අලියෙක් හාවාගේ ගෙදර කඩන්න ආවා.
                හාවා කීවා "මම ඔබට මගේ නිධානය පෙන්වන්නම්." අලියා නිධානය දැක්කම තෘප්ත වුණා. ඔවුන් මිතුරන් වුණා.
              </div>
              <ReadAloudButton
                text="කුඩා හාවෙක් වනයේ ජීවත් වුණා. ඔහු ගොඩක් බුද්ධිමත්. දිනක් ලොකු අලියෙක් හාවාගේ ගෙදර කඩන්න ආවා. හාවා කීවා මම ඔබට මගේ නිධානය පෙන්වන්නම්. අලියා නිධානය දැක්කම තෘප්ත වුණා. ඔවුන් මිතුරන් වුණා."
                label="Listen in Sinhala"
                labelSinhala="සිංහලෙන් අසන්න"
              />
              <div className="reading-english">
                A small rabbit lived in the forest. He was very clever. One day a big elephant came to break the rabbit's house.
                The rabbit said "I will show you my treasure." When the elephant saw the treasure, he was satisfied. They became friends.
              </div>
            </div>

            <div className="story-card">
              <h3>🦁 The Lion and the Mouse</h3>
              <div className="reading-sinhala">
                දිනක් ලොකු සිංහයෙක් නිදාගෙන හිටියා. කුඩා මීයෙක් සිංහයාගේ මුහුණ උඩින් දිව්වා. සිංහයා අවදි වුණා.
                ඔහු මීයා අල්ලගත්තා. මීයා කීවා "කරුණාකර මාව යන්න දෙන්න. මම ඔබට උදව් කරන්නම්."
                සිංහයා මීයාට යන්න දුන්නා.

                දවසක් දැලක් සිංහයාව අල්ලගත්තා. සිංහයාට යන්න බැරි වුණා. මීයා ආවා.
                මීයා දැල කපලා සිංහයාව බේරගත්තා. සිංහයා මීයාට ස්තූති කළා.
              </div>
              <ReadAloudButton
                text="දිනක් ලොකු සිංහයෙක් නිදාගෙන හිටියා. කුඩා මීයෙක් සිංහයාගේ මුහුණ උඩින් දිව්වා. සිංහයා අවදි වුණා. ඔහු මීයා අල්ලගත්තා. මීයා කීවා කරුණාකර මාව යන්න දෙන්න. මම ඔබට උදව් කරන්නම්. සිංහයා මීයාට යන්න දුන්නා. දවසක් දැලක් සිංහයාව අල්ලගත්තා. සිංහයාට යන්න බැරි වුණා. මීයා ආවා. මීයා දැල කපලා සිංහයාව බේරගත්තා. සිංහයා මීයාට ස්තූති කළා."
                label="Listen in Sinhala"
                labelSinhala="සිංහලෙන් අසන්න"
              />
              <div className="reading-english">
                One day a big lion was sleeping. A small mouse ran over the lion's face. The lion woke up.
                He caught the mouse. The mouse said "Please let me go. I will help you."
                The lion let the mouse go.

                One day a net caught the lion. The lion couldn't escape. The mouse came.
                The mouse cut the net and saved the lion. The lion thanked the mouse.
              </div>
            </div>

            <div className="activity-card">
              <h3>🤔 Story Questions - The Lion and the Mouse</h3>
              <p>1. What did the mouse do when the lion was sleeping?</p>
              <p>2. What did the mouse promise the lion?</p>
              <p>3. How did the mouse save the lion?</p>
              <p>4. What is the moral of this story?</p>
            </div>

            <div className="story-card">
              <h3>🌟 The Honest Woodcutter</h3>
              <div className="reading-sinhala">
                දිනක් දරු කපන්නෙක් ගඟ අසලට ගියා. ඔහුගේ පොරව ගඟට වැටුණා. ඔහු ඉතා දුකට පත් වුණා.

                දෙවියන්ගේ පණිවිඩකාරයෙක් ආවා. ඔහු රන් පොරවක් ගෙනැවිත් "මේ ඔබේ පොරවද?" කියා ඇහුවා.
                දරු කපන්නා "නෑ" කීවා. ඊට පසු රිදී පොරවක් ගෙනැවිත් ඇහුවා. දරු කපන්නා "නෑ" කීවා.

                අවසානයේ ඔහුගේම යකඩ පොරව ගෙනැවිත් දුන්නා. දරු කපන්නා "ඔව්, මේ මගේ පොරව" කීවා.
                පණිවිඩකාරයා ඔහුගේ අවංකභාවයට සතුටු වී තුන්ම පොරව ඔහුට දුන්නා.
              </div>
              <ReadAloudButton
                text="දිනක් දරු කපන්නෙක් ගඟ අසලට ගියා. ඔහුගේ පොරව ගඟට වැටුණා. ඔහු ඉතා දුකට පත් වුණා. දෙවියන්ගේ පණිවිඩකාරයෙක් ආවා. ඔහු රන් පොරවක් ගෙනැවිත් මේ ඔබේ පොරවද කියා ඇහුවා. දරු කපන්නා නෑ කීවා. ඊට පසු රිදී පොරවක් ගෙනැවිත් ඇහුවා. දරු කපන්නා නෑ කීවා. අවසානයේ ඔහුගේම යකඩ පොරව ගෙනැවිත් දුන්නා. දරු කපන්නා ඔව් මේ මගේ පොරව කීවා. පණිවිඩකාරයා ඔහුගේ අවංකභාවයට සතුටු වී තුන්ම පොරව ඔහුට දුන්නා."
                label="Listen in Sinhala"
                labelSinhala="සිංහලෙන් අසන්න"
              />
              <div className="reading-english">
                One day a woodcutter went near the river. His axe fell into the river. He became very sad.

                A messenger from the gods came. He brought a golden axe and asked "Is this your axe?"
                The woodcutter said "No." Then he brought a silver axe and asked. The woodcutter said "No."

                Finally he brought his own iron axe and gave it. The woodcutter said "Yes, this is my axe."
                The messenger was pleased with his honesty and gave him all three axes.
              </div>
            </div>
          </section>
        );

      case "practice":
        return (
          <section className="section active">
            <h2>Speak & Learn - කතා කරමින් ඉගෙන ගන්න</h2>
            <PronunciationPractice
              words={practiceWords}
              title="Practice Grade 3 Words / පන්ති 3 වචන පුහුණුව"
            />
          </section>
        );

      case "quiz":
        return (
          <section className="section active">
            <h2>Grade 3 Quiz</h2>
            <Quiz questions={quizQuestions} gradeKey="g3" gradeNumber={3} />
          </section>
        );

      default:
        return null;
    }
  };

  // Get SEO config for current section
  const seoConfig = gradeSEOConfig[3];

  return (
    <div className="grade-content active">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        keywords={seoConfig.keywords}
        canonicalPath={`/#grade3-${currentSection}`}
        structuredData={generateBreadcrumbs(3, currentSection)}
      />
      <div className="grade-info-compact">
        <h2>Grade 3 - තෙවන ශ්‍රේණිය</h2>
      </div>

      {renderSection()}

      <style>{`
        .tense-table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
        }
        .tense-table th, .tense-table td {
          border: 1px solid #ddd;
          padding: 10px;
          text-align: left;
        }
        .tense-table th {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        .tense-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .tense-table tr:hover {
          background-color: #f0f4ff;
        }
      `}</style>
    </div>
  );
};

export default Grade3;
